import Section from "./Section";
import Row from "./Row";
import { motion } from "framer-motion";
import {
  Clock,
  ChefHat,
  CheckCircle,
  XCircle,
  Package,
  ConciergeBell,
} from "lucide-react";
// ✅ your animations
import { fadeUp, fadeIn, slideLeft, slideRight } from "animations/variants";
import TrackingModal from "./TrackModal";
import { Truck } from "lucide-react";
import orderApi from "api/order/OrderApi";
import toast from "react-hot-toast";
import { useState } from "react";
const OrderInfo = ({ order, orderStatus, paymentStatus, onTrack }) => {
  console.log({ order });

  const [isPaying, setIsPaying] = useState(false);
  const formatDate = (date) => {
    if (!date) return "-";

    try {
      const d = new Date(date);

      return d.toLocaleString("en-NZ", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const badge = "py-1 px-2 rounded-full text-xs font-semibold";

  const formatLabel = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  const STATUS_LABELS = {
    pending: "Order received",
    preparing: "Kitchen is preparing your order",
    ready: "Ready for pickup",
    completed: "Order completed",
    cancelled: "Order cancelled",
    paid: "Payment successful",
  };

  const STATUS_STYLES = {
    pending: {
      badge: "bg-gray-100 text-gray-600 border border-gray-300",
    },
    preparing: {
      badge: "bg-yellowlight/30 text-yellowdark border border-yellowdark",
    },
    ready: {
      badge: "bg-blue-100 text-blue-700 border border-blue-400",
    },
    completed: {
      badge: "bg-green-100 text-green-700 border border-green-400",
    },
    cancelled: {
      badge: "bg-red-100 text-red-600 border border-red-400",
    },
  };

  const PAYMENT_STYLES = {
    paid: {
      badge: "bg-green-500/10 text-green-500 border border-green-400",
    },
    pending: {
      badge: "text-neonYellow border border-gray-200",
    },
    refunded: {
      badge: "bg-red-500/10 text-red-500 border border-red-400",
    },
  };

  const STATUS_ICONS = {
    pending: <Clock size={16} />,
    preparing: <ChefHat size={16} />,
    ready: <ConciergeBell size={16} />,
    completed: <CheckCircle size={16} />,
    cancelled: <XCircle size={16} />,
  };

  const buildStripePayloadFromOrder = (order) => {
    return {
      id: order._id,
      email: order.customer_info.email,

      orderItems: order.orderItems.map((item) => ({
        name: item.title, // ✅ FIX name
        price: item.price,
        qty: item.qty,
        image: item.foodItem?.image || "",
        product: item.foodItem?._id,
      })),
    };
  };

  const handlePayNow = async (order) => {
    try {
      setIsPaying(true);

      const payload = buildStripePayloadFromOrder(order);

      const { response, err } = await orderApi.stripeCheckout(payload);

      if (err) throw new Error(err.message);

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.message || "Stripe checkout failed");
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <motion.div variants={slideLeft}>
      <Section title="Order Details">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-2">
            <Row label="Order Date" value={formatDate(order?.createdAt)} />
            <Row
              label="Order Status"
              value={
                <div className="flex flex-col items-end gap-1 ">
                  {/* BADGE */}
                  <span
                    className={`${badge} ${
                      STATUS_STYLES[orderStatus]?.badge
                    } inline-flex items-center justify-center gap-1 min-w-[140px] text-center py-2 `}
                  >
                    {STATUS_ICONS[orderStatus]}
                    {STATUS_LABELS[orderStatus] || formatLabel(orderStatus)}
                  </span>
                </div>
              }
            />
          </div>

          <div className="space-y-2">
            <Row
              label="Payment Method"
              value={
                <span className="text-md text-text font-light uppercase">
                  {order?.payments?.paymentMethod}
                </span>
              }
            />

            <Row
              label="Payment Status"
              value={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* STATUS BADGE */}
                  <div
                    className={`
          relative overflow-hidden
          inline-flex items-center gap-2
          px-4 py-2 rounded-2xl
          border backdrop-blur-sm
          shadow-sm
          transition-all duration-300
          ${PAYMENT_STYLES[paymentStatus]?.badge}
        `}
                  >
                    {/* animated glow */}
                    {paymentStatus === "refunded" && (
                      <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-transparent to-red-500/5 animate-pulse" />
                    )}

                    {/* icon */}
                    <div className="relative z-10">
                      {paymentStatus === "paid" && (
                        <CheckCircle size={16} className="text-green-500" />
                      )}

                      {paymentStatus === "pending" && (
                        <Clock size={16} className="text-yellowstar" />
                      )}

                      {paymentStatus === "refunded" && (
                        <div className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>

                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </div>
                      )}
                    </div>

                    {/* text */}
                    <div className="relative z-10 flex  gap-x-1 items-start leading-tight">
                      <span className="text-xs font-bold tracking-wide uppercase">
                        {formatLabel(paymentStatus)}
                      </span>

                      {paymentStatus === "paid" && (
                        <span className="text-[10px] opacity-70">
                          Payment successful
                        </span>
                      )}

                      {paymentStatus === "pending" && (
                        <span className="text-[10px] opacity-70">
                          Awaiting payment
                        </span>
                      )}

                      {paymentStatus === "refunded" && (
                        <span className="text-[10px] opacity-70">
                          Refunded to customer
                        </span>
                      )}
                    </div>
                  </div>

                  {/* PAY NOW */}
                  {order?.payments?.paymentStatus === "pending" && (
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handlePayNow(order)}
                      className="
            inline-flex items-center gap-2
            px-4 py-2 rounded-2xl
            bg-gradient-to-r from-yellowstar to-yellowlight
            text-navy font-bold text-xs
            shadow-md hover:shadow-lg
            transition-all duration-300
          "
                    >
                      <Package size={14} />

                      <span>Pay Now ${order.totalPrice.toFixed(2)}</span>
                    </motion.button>
                  )}
                </div>
              }
            />
            {/* <Row
              label="Payment Status"
              value={
                <div className="flex items-end gap-1">
                  <span
                    className={`${badge} ${
                      PAYMENT_STYLES[paymentStatus]?.badge
                    } inline-block min-w-[100px] text-center`}
                  >
                    {" "}
                    {paymentStatus === "refunded" && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>

                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    )}
                    {formatLabel(paymentStatus)}
                  </span>
                  {order?.payments?.paymentStatus === "pending" && (
                    <button
                      onClick={() => handlePayNow(order)}
                      className="px-4 py-1 text-xs rounded-full border border-yellowstar bg-none hover:bg-navy text-yellowstar text-sm font-semibold transition"
                    >
                      Pay Now ${order.totalPrice.toFixed(2)}
                    </button>
                  )}
                </div>
              }
            />{" "} */}
          </div>
        </div>

        <div className="pt-3">
          <motion.button
            onClick={onTrack}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neonYellow bg-neonYellow/20 text-sm text-neonYellow font-medium hover:bg-navy/20  transition-all duration-200"
          >
            <Truck size={16} />
            <span className="text-neonYellow animate-neonFlicker">
              Track Order
            </span>
          </motion.button>
        </div>
      </Section>
    </motion.div>
  );
};

export default OrderInfo;
